import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Plus,
  X,
  Clock,
  Brain,
  Heart,
  Pill,
  Mail,
  Smartphone,
  Trash2,
  Check,
  Calendar,
  Repeat
} from 'lucide-react';
import { Header } from '../components/layout/Header';
import { remindersAPI, Reminder, ReminderFormData } from '../services/api';
import { useSubscription } from '../context/SubscriptionContext';
import { UpgradePrompt } from '../components/subscription/UpgradePrompt';

const typeOptions = [
  { value: 'headache', label: 'Dolor de cabeza', icon: Brain, color: '#E8927C' },
  { value: 'tension', label: 'Tensión arterial', icon: Heart, color: '#9CAF88' },
  { value: 'medication', label: 'Medicación', icon: Pill, color: '#7DD3FC' },
];

const frequencyOptions = [
  { value: 'daily', label: 'Diario' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'once', label: 'Una vez' },
];

const notificationOptions = [
  { value: 'push', label: 'Push (PWA)', icon: Smartphone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'both', label: 'Ambos', icon: Bell },
];

export const RemindersPage: React.FC = () => {
  const { canUse } = useSubscription();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<ReminderFormData>({
    type: 'headache',
    title: '',
    message: '',
    frequency: 'daily',
    time: '09:00',
    notification_method: 'push',
    is_active: true,
  });

  useEffect(() => {
    loadReminders();
  }, []);

  const loadReminders = async () => {
    setIsLoading(true);
    try {
      const response = await remindersAPI.getAll();
      setReminders(response.data.reminders || []);
    } catch (err) {
      console.error('Error loading reminders:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingReminder(null);
    setFormData({
      type: 'headache',
      title: '',
      message: '',
      frequency: 'daily',
      time: '09:00',
      notification_method: 'push',
      is_active: true,
    });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (reminder: Reminder) => {
    setEditingReminder(reminder);
    setFormData({
      type: reminder.type,
      title: reminder.title,
      message: reminder.message || '',
      frequency: reminder.frequency,
      time: reminder.time.substring(0, 5),
      notification_method: reminder.notification_methods.includes('both') ? 'both' :
        reminder.notification_methods.includes('email') ? 'email' : 'push',
      is_active: reminder.enabled,
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('El título es obligatorio');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      if (editingReminder) {
        await remindersAPI.update(editingReminder.id, formData);
      } else {
        await remindersAPI.create(formData);
      }
      await loadReminders();
      setShowModal(false);
    } catch (err: any) {
      setError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este recordatorio?')) return;

    try {
      await remindersAPI.delete(id);
      await loadReminders();
    } catch (err) {
      console.error('Error deleting reminder:', err);
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await remindersAPI.toggle(id);
      await loadReminders();
    } catch (err) {
      console.error('Error toggling reminder:', err);
    }
  };

  const getTypeInfo = (type: string) => {
    return typeOptions.find(t => t.value === type) || typeOptions[0];
  };

  const getFrequencyLabel = (frequency: string) => {
    return frequencyOptions.find(f => f.value === frequency)?.label || frequency;
  };

  const closeModal = () => setShowModal(false);

  if (!canUse('reminders')) {
    return (
      <div>
        <Header title="Recordatorios" />
        <main className="px-4 sm:px-6 pb-24 lg:pb-6 max-w-2xl lg:max-w-4xl mx-auto">
          <UpgradePrompt feature="reminders" />
        </main>
      </div>
    );
  }

  return (
    <div>
      <Header title="Recordatorios" />

      <main className="px-4 sm:px-6 pb-24 lg:pb-6 max-w-2xl lg:max-w-4xl mx-auto">
        {/* Add Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileTap={{ scale: 0.98 }}
          onClick={openAddModal}
          className="w-full mb-6 p-4 rounded-2xl border-2 border-dashed border-[var(--color-border)] text-[var(--color-text-muted)] font-medium flex items-center justify-center gap-2 hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors"
        >
          <Plus size={20} />
          Añadir recordatorio
        </motion.button>

        {/* Reminders List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-3 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : reminders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="card p-8 text-center"
          >
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-[var(--color-accent-soft)]">
              <Bell size={32} className="text-[var(--color-accent)]" />
            </div>
            <p className="text-[var(--color-text)] font-medium mb-1">Sin recordatorios</p>
            <p className="text-sm text-[var(--color-text-muted)]">
              Añade recordatorios para no olvidar registrar tus síntomas
            </p>
          </motion.div>
        ) : (
          <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
            {reminders.map((reminder, index) => {
              const typeInfo = getTypeInfo(reminder.type);
              const TypeIcon = typeInfo.icon;

              return (
                <motion.div
                  key={reminder.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`card p-4 ${!reminder.enabled ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${typeInfo.color}20` }}
                    >
                      <TypeIcon size={24} style={{ color: typeInfo.color }} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-[var(--color-text)] truncate">
                          {reminder.title}
                        </h3>
                        {reminder.notification_methods.includes('email') && (
                          <Mail size={14} className="text-[var(--color-text-muted)] flex-shrink-0" />
                        )}
                        {reminder.notification_methods.includes('push') && (
                          <Smartphone size={14} className="text-[var(--color-text-muted)] flex-shrink-0" />
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-text-muted)]">
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {reminder.time.substring(0, 5)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Repeat size={14} />
                          {getFrequencyLabel(reminder.frequency)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={reminder.enabled}
                          onChange={() => handleToggle(reminder.id)}
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-accent)]"></div>
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3 pt-3 border-t border-[var(--color-border)]">
                    <button
                      onClick={() => openEditModal(reminder)}
                      className="flex-1 py-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(reminder.id)}
                      className="flex-1 py-2 text-sm text-red-500 hover:text-red-600 transition-colors flex items-center justify-center gap-1"
                    >
                      <Trash2 size={14} />
                      Eliminar
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-6 p-4 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)]"
        >
          <p className="text-sm text-[var(--color-text-muted)]">
            <strong>Push (PWA):</strong> Requiere que la app esté instalada y permisos de notificación.
            <br />
            <strong>Email:</strong> Recibirás un correo en la hora indicada.
          </p>
        </motion.div>
      </main>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={closeModal}
            />

            {/* Mobile: Bottom sheet */}
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="lg:hidden fixed bottom-0 left-0 right-0 z-[101] p-6 pb-safe rounded-t-3xl max-h-[90vh] overflow-y-auto"
              style={{ background: 'var(--color-surface)' }}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-display font-semibold text-[var(--color-text)]">
                  {editingReminder ? 'Editar recordatorio' : 'Nuevo recordatorio'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
                >
                  <X size={24} className="text-[var(--color-text-muted)]" />
                </button>
              </div>
              <FormContent
                formData={formData}
                setFormData={setFormData}
                error={error}
                isSaving={isSaving}
                onSave={handleSave}
                isEditing={!!editingReminder}
              />
            </motion.div>

            {/* Desktop: Centered modal */}
            <div className="hidden lg:flex fixed inset-0 z-[101] items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-[var(--color-surface)] rounded-3xl shadow-xl p-6 pointer-events-auto max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-display font-semibold text-[var(--color-text)]">
                    {editingReminder ? 'Editar recordatorio' : 'Nuevo recordatorio'}
                  </h3>
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
                  >
                    <X size={24} className="text-[var(--color-text-muted)]" />
                  </button>
                </div>
                <FormContent
                  formData={formData}
                  setFormData={setFormData}
                  error={error}
                  isSaving={isSaving}
                  onSave={handleSave}
                  isEditing={!!editingReminder}
                />
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// Form content component
interface FormContentProps {
  formData: ReminderFormData;
  setFormData: React.Dispatch<React.SetStateAction<ReminderFormData>>;
  error: string;
  isSaving: boolean;
  onSave: () => void;
  isEditing: boolean;
}

const FormContent: React.FC<FormContentProps> = ({
  formData,
  setFormData,
  error,
  isSaving,
  onSave,
  isEditing,
}) => {
  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Type Selection */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          Tipo de recordatorio
        </label>
        <div className="grid grid-cols-3 gap-2">
          {typeOptions.map((option) => (
            <motion.button
              key={option.value}
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={() => setFormData({ ...formData, type: option.value as any })}
              className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                formData.type === option.value
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                  : 'border-transparent bg-[var(--color-bg)]'
              }`}
            >
              <option.icon size={24} style={{ color: option.color }} />
              <span className="text-xs text-[var(--color-text)]">{option.label.split(' ')[0]}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          Título *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="input"
          placeholder="Ej: Tomar tensión por la mañana"
        />
      </div>

      {/* Time */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          <Clock size={16} className="inline mr-1" />
          Hora
        </label>
        <input
          type="time"
          value={formData.time}
          onChange={(e) => setFormData({ ...formData, time: e.target.value })}
          className="input"
        />
      </div>

      {/* Frequency */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          <Repeat size={16} className="inline mr-1" />
          Frecuencia
        </label>
        <div className="grid grid-cols-4 gap-2">
          {frequencyOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFormData({ ...formData, frequency: option.value as any })}
              className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                formData.frequency === option.value
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'bg-[var(--color-bg)] text-[var(--color-text)]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notification Method */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          <Bell size={16} className="inline mr-1" />
          Método de notificación
        </label>
        <div className="grid grid-cols-3 gap-2">
          {notificationOptions.map((option) => (
            <motion.button
              key={option.value}
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={() => setFormData({ ...formData, notification_method: option.value as any })}
              className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                formData.notification_method === option.value
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                  : 'border-transparent bg-[var(--color-bg)]'
              }`}
            >
              <option.icon size={20} className="text-[var(--color-text)]" />
              <span className="text-xs text-[var(--color-text)]">{option.label}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Message (optional) */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          Mensaje (opcional)
        </label>
        <textarea
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="input min-h-[80px] resize-none"
          placeholder="Añade un mensaje personalizado..."
        />
      </div>

      {/* Save Button */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={onSave}
        disabled={isSaving}
        className="btn btn-primary w-full py-4 mt-4"
      >
        {isSaving ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <Check size={20} />
            {isEditing ? 'Guardar cambios' : 'Crear recordatorio'}
          </>
        )}
      </motion.button>
    </div>
  );
};
